import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";
import UserDigest from "../../types/core/UserDigest";
import File from "../../types/files/File";

import Datastore from "../../communication/Datastore";

import store, { IAppState } from "../../state/Store";

import Folder from "../../types/files/Folder";
import { getDigestDetails } from "../core/User";
import { unwrappedDispatch } from "../../foundation/Dispatching";
import BrowserFile from "../../types/files/BrowserFile";
import { SystemFileType } from "../../types/enums/SystemFileType";
import { SystemFileVersion } from "../../types/enums/SystemFileVersion";
import { Guid } from "guid-typescript";
import Utility from "../../foundation/Utility";

export interface FileUploadState {
    id: Guid;
    name: String;
    progress: number;
}

// Define the type for our state store entry
type FoldersState = {
    root: Folder;
    files: Record<number, File[]>;
    paths: Record<number, Folder[]>;
    open: number;
    viewed: File[];
    path: Folder[];
    allStructure: File[];
    uploads: FileUploadState[];
    recycled: File[];
    status: string;
    contentStatus: string;
    allStructureStatus: string;
    recycledStatus: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: FoldersState = {
    root: null,
    files: {},
    paths: {},
    open: -1,
    viewed: [],
    path: [],
    allStructure: [],
    uploads: [],
    recycled: [],
    status: "idle",
    contentStatus: "idle",
    allStructureStatus: "idle",
    recycledStatus: "idle",
    error: null,
};

interface viewPackage {
    id: number;
    data: File[];
    path: Folder[];
}

// Create a slice to hold all our reducers
const foldersSlice = createSlice({
    name: "folders",
    initialState: initialState,
    reducers: {
        setViewData: (state, action: PayloadAction<viewPackage>) => {
            state.files[action.payload.id] = action.payload.data.map(
                item => new File(item),
            );
            state.paths[action.payload.id] = action.payload.path.map(
                item => new Folder(item),
            );
        },
        clearViewData: (state, action: PayloadAction<number>) => {
            const tempFiles = { ...state.files };
            delete tempFiles[action.payload];
            const tempPaths = { ...state.paths };
            delete tempPaths[action.payload];
            state.files = tempFiles;
            state.paths = tempPaths;
            state.contentStatus = "idle";
        },
        setView: (state, action: PayloadAction<number>) => {
            state.open = action.payload;
            state.viewed = state.files[action.payload];
            state.path = state.paths[action.payload];
            for (const file of state.viewed ?? []) {
                switch (file.fileType) {
                    case "TimetableFile":
                        file.badge = "T";
                        break;
                    case "StudentOptionsFile":
                        file.badge = "S";
                        break;
                    case "DailyOrganiserFile":
                        file.badge = "D";
                        break;
                }
                file.typeDisplay =
                    (file.version === SystemFileVersion.Version8
                        ? "8"
                        : file.version === SystemFileVersion.Version9
                        ? "9"
                        : file.version === SystemFileVersion.Version10
                        ? "10"
                        : "Unknown") +
                    " " +
                    (file.fileType === SystemFileType.TimetableFile
                        ? "Timetable"
                        : file.fileType === SystemFileType.StudentOptionsFile
                        ? "Student Options"
                        : file.fileType === SystemFileType.DailyOrganiserFile
                        ? "Daily Organiser"
                        : "");
            }
        },
        clearAllStructure: state => {
            state.allStructure = [];
            state.allStructureStatus = "idle";
        },
        appendFolder: (state, action: PayloadAction<Folder>) => {
            const file = new File(action.payload);
            file.fileType = SystemFileType.Folder;
            file.fileId = action.payload.id;
            file.folderId = action.payload.parentID;
            file.isFolder = true;
            file.schoolId = action.payload.schoolID;

            state.viewed = [...state.viewed, file];
            state.files = {
                ...state.files,
                [state.open]: [...state.files[state.open], file],
            };
            state.allStructure = [...state.allStructure, file];
        },
        moveFilesInternal: (state, action: PayloadAction<FileCopyMove>) => {
            action.payload.files.forEach(moving => {
                const current = state.files[state.open].find(
                    item =>
                        item.fileType === moving.fileType &&
                        item.id === moving.id,
                );

                if (current) {
                    state.files[state.open].splice(
                        state.files[state.open].indexOf(current),
                        1,
                    );

                    if (state.files[action.payload.target])
                        state.files[action.payload.target].push(current);
                }
            });
        },
        renameFileFolder: (state, action: PayloadAction<File>) => {
            const mainEntry = state.files[state.open]?.find(
                item =>
                    item.fileType === action.payload.fileType &&
                    item.id === action.payload.id,
            );
            const viewEntry = state.viewed?.find(
                item =>
                    item.fileType === action.payload.fileType &&
                    item.id === action.payload.id,
            );
            const allEntry = state.allStructure?.find(
                item =>
                    item.fileType === action.payload.fileType &&
                    item.id === action.payload.id,
            );

            if (mainEntry) {
                mainEntry.name = action.payload.name;
                mainEntry.fileName = action.payload.fileName;

                state.files = {
                    ...state.files,
                    [state.open]: state.files[state.open]
                        .filter(
                            item =>
                                item.fileType !== mainEntry.fileType ||
                                item.id !== mainEntry.id,
                        )
                        .concat([mainEntry]),
                };
            }

            if (viewEntry) {
                viewEntry.name = action.payload.name;
                viewEntry.fileName = action.payload.fileName;

                state.viewed = [
                    ...state.viewed.filter(
                        item =>
                            item.fileType !== viewEntry.fileType ||
                            item.id !== viewEntry.id,
                    ),
                    viewEntry,
                ];
            }

            if (allEntry) {
                allEntry.name = action.payload.name;
                allEntry.fileName = action.payload.fileName;

                state.allStructure = [
                    ...state.allStructure.filter(
                        item =>
                            item.fileType !== allEntry.fileType ||
                            item.id !== allEntry.id,
                    ),
                    allEntry,
                ];
            }
        },
        removeFromView: (state, action: PayloadAction<File[]>) => {
            for (const file of action.payload) {
                state.files[state.open] = state.files[state.open].filter(
                    item =>
                        item.fileType !== file.fileType || item.id !== file.id,
                );

                state.viewed = state.viewed.filter(
                    item =>
                        item.fileType !== file.fileType || item.id !== file.id,
                );

                state.allStructure = state.allStructure.filter(
                    item =>
                        item.fileType !== file.fileType || item.id !== file.id,
                );

                if (file.isFolder && state.paths[file.id])
                    state.paths = { ...state.paths, [file.id]: undefined };

                if (file.isFolder && state.files[file.id])
                    state.files = { ...state.files, [file.id]: undefined };
            }

            if (state.paths[state.open])
                state.paths = { ...state.paths, [state.open]: undefined };
        },
        resetAllViews: state => {
            state.files = {};
        },
        updateRecycled: (state, action: PayloadAction<File[]>) => {
            for (const entry of action.payload) {
                state.recycled = state.recycled.filter(
                    item => item.fileId !== entry.fileId,
                );
                state.recycled.push(entry);
            }
        },
        resetRecycled: state => {
            state.recycled = [];
        },
        addUpload: (state, action: PayloadAction<FileUploadState>) => {
            state.uploads.push(action.payload);
        },
        addUploads: (state, action: PayloadAction<FileUploadState[]>) => {
            state.uploads.push(...action.payload);
        },
        updateUpload: (state, action: PayloadAction<FileUploadState>) => {
            const entry = state.uploads.find(
                item => item.name === action.payload.name,
            );

            if (entry) entry.progress = action.payload.progress;
        },
        removeUpload: (state, action: PayloadAction<Guid>) => {
            state.uploads = state.uploads.filter(
                item => item.id !== action.payload,
            );
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;

                const files = {},
                    paths = {};

                if (rawData?.payload?.folders?.files)
                    for (let entry in rawData?.payload?.folders.files)
                        files[entry] = rawData?.payload?.folders.files[
                            entry
                        ].map(item => new File(item));

                if (rawData?.payload?.folders?.paths)
                    for (let entry in rawData?.payload?.folders.paths)
                        paths[entry] = rawData?.payload?.folders.paths[
                            entry
                        ].map(item => new Folder(item));

                return {
                    root: rawData?.payload?.folders?.root
                        ? new Folder(rawData?.payload?.folders?.root)
                        : null,
                    files: files,
                    paths: paths,
                    open: rawData?.payload?.folders?.open,
                    viewed: rawData?.payload?.folders?.viewed
                        ? rawData?.payload?.folders?.viewed.map(
                              item => new File(item),
                          )
                        : [],
                    path: rawData?.payload?.folders?.path
                        ? rawData?.payload?.folders?.path.map(
                              item => new Folder(item),
                          )
                        : [],
                    allStructure: rawData?.payload?.folders?.allStructure
                        ? rawData?.payload?.folders?.allStructure.map(
                              item => new File(item),
                          )
                        : [],
                    recycled: rawData?.payload?.folders?.recycled
                        ? rawData?.payload?.folders?.recycled.map(
                              item => new File(item),
                          )
                        : [],
                    status: !rawData?.payload?.folders?.root
                        ? "idle"
                        : rawData?.payload?.folders?.status,
                    contentStatus: !rawData?.payload?.folders?.files
                        ? "idle"
                        : rawData?.payload?.folders?.contentStatus,
                    allStructureStatus: !rawData?.payload?.folders?.allStructure
                        ?.length
                        ? "idle"
                        : rawData?.payload?.folders?.allStructureStatus,
                    uploads: initialState.uploads,
                    recycledStatus: !rawData?.payload?.folders?.recycled
                        ? "idle"
                        : rawData?.payload?.folders?.recycledStatus ?? "idle",
                    error: null,
                };
            })
            .addCase(fetchFolders.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchFolders.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.root = action.payload ? new Folder(action.payload) : null;
            })
            .addCase(fetchFolders.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchContents.pending, (state, action) => {
                state.contentStatus = "loading";
            })
            .addCase(fetchContents.fulfilled, (state, action) => {
                state.contentStatus = "succeeded";
            })
            .addCase(fetchContents.rejected, (state, action) => {
                state.contentStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchAllStructure.pending, (state, action) => {
                state.allStructureStatus = "loading";
            })
            .addCase(fetchAllStructure.fulfilled, (state, action) => {
                state.allStructure = action.payload.map(item => new File(item));
                state.allStructureStatus = "succeeded";
            })
            .addCase(fetchAllStructure.rejected, (state, action) => {
                state.allStructureStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchRecycled.pending, (state, action) => {
                state.recycledStatus = "loading";
            })
            .addCase(fetchRecycled.fulfilled, (state, action) => {
                state.recycledStatus = "succeeded";
                state.recycled =
                    action.payload?.map(item => {
                        const file = new File(item);

                        switch (file.fileType) {
                            case "Folder":
                                file.code = "Folder";
                                break;
                            case "TimetableFile":
                                file.code = "TDF";
                                break;
                            case "StudentOptionsFile":
                                file.code = "SOF";
                                break;
                            case "DailyOrganiserFile":
                                file.code = "DOF";
                                break;
                        }

                        return file;
                    }) ?? [];
            })
            .addCase(fetchRecycled.rejected, (state, action) => {
                state.recycledStatus = "failed";
                state.error = action.error.message;
            });
    },
});

const {
    setView,
    setViewData,
    clearViewData,
    clearAllStructure,
    appendFolder,
    moveFilesInternal,
    renameFileFolder,
    removeFromView,
    resetAllViews,
    updateRecycled,
    resetRecycled,
    addUploads,
    updateUpload,
    removeUpload,
} = foldersSlice.actions;

// This is a thunk, they are used to load the state
export const fetchFolders = createAsyncThunk(
    "folders/fetchFolders",
    async (): Promise<Folder> => {
        return await Datastore.getType<Folder>("api/Folders");
    },
    {
        condition: (_, { getState }) => {
            const { folders } = (getState() as IAppState).db;
            return folders.status !== "loading";
        },
    },
);
export const fetchContents = createAsyncThunk(
    "folders/fetchContents",
    async (id: number): Promise<[Folder[], File[]]> => {
        return Promise.all([
            await Datastore.getType<Folder[]>(`api/Folders/${id}/Path`),
            await Datastore.getType<File[]>(`api/SystemIO/Folders/${id}/Files`),
        ]);
    },
);
export const initialiseFolders = createAsyncThunk(
    "folders/initialiseFolders",
    async () => {
        await unwrappedDispatch(fetchFolders());

        const { folders } = store.getState().db;

        if (!folders.root) return;

        await getFolderContents(folders.root.id);
    },
    {
        condition: (_, { getState }) => {
            const { folders } = (getState() as IAppState).db;
            return folders.contentStatus === "idle";
        },
    },
);
export const getFolderContents = async (id: number) => {
    const { folders } = store.getState().db;

    if (
        (folders.open === id && folders.files[id] && folders.paths[id]) ||
        folders.contentStatus === "loading"
    )
        return;

    if (!folders.files[id] || !folders.paths[id]) {
        const data = await unwrappedDispatch(fetchContents(id));
        store.dispatch(
            setViewData({
                id: id,
                path: data[0],
                data: data[1],
            }),
        );
    }

    store.dispatch(setView(id));
};
export const clearFolderContents = (reset: boolean = false) => {
    const { folders } = store.getState().db;

    if (
        !!folders.open &&
        (!!folders.files[folders.open] || !!folders.paths[folders.open])
    )
        store.dispatch(clearViewData(folders.open));

    store.dispatch(clearAllStructure());
};
export const reloadFolderContents = async () => {
    const { folders } = store.getState().db;

    clearFolderContents();

    await Promise.all([
        getFolderContents(folders.open),
        unwrappedDispatch(fetchAllStructure()),
    ]);
    store.dispatch(setView(folders.open));
};
export const fetchAllStructure = createAsyncThunk(
    "folders/fetchAllStructure",
    async (): Promise<File[]> => {
        return await Datastore.getType<File[]>("api/SystemIO/Files");
    },
    {
        condition: (_, { getState }) => {
            const { folders } = (getState() as IAppState).db;
            return folders.allStructureStatus !== "loading";
        },
    },
);
export const initialiseAllStructure = async () => {
    await unwrappedDispatch(initialiseFolders());
    await unwrappedDispatch(fetchAllStructure());
};
export const uploadFile = async (folderId: number, files: BrowserFile[]) => {
    const uploads: {
        name: String;
        type: String;
        version: String;
        data: string;
    }[] = [];

    for (const file of files) {
        switch (file.extn.toLowerCase()) {
            case ".tdf9":
            case ".ptf9":
                uploads.push({
                    name: file.name,
                    type: "TDF",
                    version: "V9",
                    data: file.data,
                });
                break;
            case ".tfx":
                uploads.push({
                    name: file.name,
                    type: "TDF",
                    version: "V10",
                    data: file.data,
                });
                break;
            case ".sof9":
                uploads.push({
                    name: file.name,
                    type: "SOF",
                    version: "V9",
                    data: file.data,
                });
                break;
            case ".sfx":
                uploads.push({
                    name: file.name,
                    type: "SOF",
                    version: "V10",
                    data: file.data,
                });
                break;
            case ".dof9":
                uploads.push({
                    name: file.name,
                    type: "DOF",
                    version: "V9",
                    data: file.data,
                });
                break;
            case ".zip":
                uploads.push({
                    name: file.name,
                    type: "ZIP",
                    version: "",
                    data: file.data,
                });
                break;
        }
    }

    const transmit = uploads.groupBy(item => item.type);
    const waits = [];
    const uploadsState = new Array<FileUploadState>();

    for (const group of transmit) {
        let pathKey = "";
        switch (group[0]) {
            case "TDF":
                pathKey = "TD/";
                break;
            case "SOF":
                pathKey = "SO/";
                break;
            case "DOF":
                pathKey = "DO/";
                break;
            case "ZIP":
                pathKey = "";
                break;
        }

        for (const entry of group[1]) {
            waits.push(
                (async () => {
                    const state = {
                        id: Utility.newGuid(),
                        name: entry.name,
                        progress: 0,
                    };

                    uploadsState.push(state);

                    const promise = Datastore.post(
                        `api/SystemIO/${pathKey}Folder/${folderId}/Name/` +
                            (group[0] === "ZIP"
                                ? "Compressed"
                                : `Version/${entry.version}`),
                        {
                            name: entry.name,
                            data: entry.data,
                        },
                    );

                    promise.progress(progress => {
                        store.dispatch(
                            updateUpload({
                                ...state,
                                progress:
                                    (progress.loaded / progress.total) * 100,
                            }),
                        );
                    });
                    promise.finally(() => {
                        store.dispatch(removeUpload(state.id));
                    });

                    await promise;
                })(),
            );
        }
    }

    store.dispatch(addUploads(uploadsState));
    await Promise.all(waits);

    await reloadFolderContents();
};
export const downloadFile = async (files: File[]) => {
    const result = await Datastore.post(`api/SystemIO/Files/Compressed`, files);

    (result as string).download(
        "TDSProjectFolderFileDownload_" + new Date().toTimestamp() + ".zip",
        "application/zip",
    );

    await reloadFolderContents();
};
export const addFolder = async (name: string, folderId: number) => {
    const { folders } = store.getState().db;

    const newFolder = await Datastore.postType<Folder>("api/Folders", [
        {
            parentID: folderId ?? folders.open,
            name: name,
        },
    ]);

    store.dispatch(appendFolder(newFolder[0]));
};
export interface FileRename {
    file: File;
    name: string;
}
export const renameFiles = async (renames: FileRename[]) => {
    await Promise.all(
        renames.map(rename => {
            switch (rename.file.fileType.toUpperCase()) {
                case "TIMETABLEFILE":
                    return Datastore.post(
                        `api/SystemIO/TD/${rename.file.id}/Rename`,
                        rename.name,
                    );
                case "STUDENTOPTIONSFILE":
                    return Datastore.post(
                        `api/SystemIO/SO/${rename.file.id}/Rename`,
                        rename.name,
                    );
                case "DAILYORGANISERFILE":
                    return Datastore.post(
                        `api/SystemIO/DO/${rename.file.id}/Rename`,
                        rename.name,
                    );
                case "FOLDER":
                    return Datastore.post(
                        `api/Folders/${rename.file.id}/Rename`,
                        rename.name,
                    );
                default:
                    return Promise.resolve();
            }
        }),
    );

    renames.forEach(rename => {
        rename.file.name = rename.name;
        rename.file.fileName = rename.name;
        store.dispatch(renameFileFolder(rename.file));
    });
};
export interface FileCopyMove {
    files: File[];
    target: number;
}
export const moveFiles = createAsyncThunk(
    "folders/moveFiles",
    async (moves: FileCopyMove, thunkAPI) => {
        const { folders } = store.getState().db;

        await Datastore.post(
            `api/SystemIO/Folders/${moves.target}/Files/Move`,
            moves.files,
        );

        thunkAPI.dispatch(moveFilesInternal(moves));
        thunkAPI.dispatch(setView(folders.open));
    },
);
export const copyFiles = createAsyncThunk(
    "folders/copyFiles",
    async (copies: FileCopyMove, thunkAPI) => {
        const { folders } = store.getState().db;

        copies.files.forEach(item => (item.fileName = item.name));

        await Datastore.post(
            `api/SystemIO/Folders/${copies.target}/Files/CopyNew`,
            copies.files,
        );

        if (copies.target === folders.open) await reloadFolderContents();
        else store.dispatch(clearViewData(copies.target));
    },
);
export const deleteFiles = async (items: File[]) => {
    await Datastore.post(
        "api/SystemIO/Files/Recycle",
        items.map(item => ({
            fileType: item.fileType,
            fileId: item.fileId,
        })),
    );

    store.dispatch(removeFromView(items));
};
export const permDeleteFiles = createAsyncThunk(
    "folders/permDeleteFiles",
    async (items: File[], thunkAPI) => {
        await Datastore.post(
            "api/SystemIO/Files/Delete",
            items.map(item => ({
                fileType: item.fileType,
                fileId: item.fileId,
            })),
        );

        thunkAPI.dispatch(removeFromView(items));
    },
);
export const restoreFiles = createAsyncThunk(
    "folders/restoreFiles",
    async (items: File[], thunkAPI) => {
        await Datastore.post(
            "api/SystemIO/Files/Restore",
            items.map(item => ({
                fileType: item.fileType,
                fileId: item.fileId,
            })),
        );

        thunkAPI.dispatch(resetAllViews());
        await reloadFolderContents();
    },
);
export const fetchRecycled = createAsyncThunk(
    "folders/fetchRecycled",
    async (_, thunkAPI): Promise<File[]> => {
        const result = await Datastore.getType<File[]>(
            "api/SystemIO/Files/Recycled",
        );

        const users = await thunkAPI.dispatch(
            getDigestDetails(
                result
                    .filter(item => item.recycledBy)
                    .map(item => item.recycledBy),
            ),
        );

        for (let file of result) {
            if (file.recycledBy) {
                const user = (users.payload as UserDigest[]).find(
                    user => user.id === file.recycledBy,
                );

                file.recycledByUser = user;
            }
        }

        thunkAPI.dispatch(updateRecycled(result));

        return result;
    },
);
export const initialiseRecycled = createAsyncThunk(
    "folders/initialiseRecycled",
    async (_, thunkAPI) => {
        const { folders } = store.getState().db;

        if (
            folders.recycledStatus === "idle" ||
            folders.recycledStatus === "succeeded"
        ) {
            thunkAPI.dispatch(resetRecycled());
            await thunkAPI.dispatch(fetchRecycled());
        }
    },
);

// We have to export the reducer as this is added to the state store
export default foldersSlice.reducer;
